import { action } from '@ember/object';
import { next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import SharedArtifactEntryPoint from '@databox/ember-integrations/lib/components/Ai/SharedArtifact/SharedArtifactEntryPoint';

import type { ReactIntegrations, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { getShadowDomMode } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';

interface SharedArtifactSignature {
	Element: HTMLDivElement;
	Args: {
		shareToken: string;
	};
}

export default class GenieSharedArtifactComponent extends Component<SharedArtifactSignature> {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account') declare accountService: Service.Account;
	@service('react') declare reactService: Service.React;
	@service('request') declare requestService: Service.Request;

	reactElement: ReactIntegrations.Root = null;

	@action
	setupView(node: HTMLElement) {
		this.reactElement = this.reactService.create(node as HTMLDivElement);

		schedule('afterRender', () => {
			next(() => {
				this.renderSharedArtifact();
			});
		});

		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderSharedArtifact);
		this.eventManagerService.on(
			AppEvent.ACCESS_TOKEN_REFRESHED,
			this,
			this.renderSharedArtifact,
		);
	}

	@action
	teardownView() {
		this.reactService.unmount(this.reactElement);
		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
		this.eventManagerService.off(AppEvent.ACCESS_TOKEN_REFRESHED, this);
	}

	private renderSharedArtifact() {
		if (!this.reactElement) {
			return;
		}

		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		const props = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			accessToken,
			dataToken,
			spaceId: this.accountService.id,
			user: {
				id: Number(this.currentUserService.id),
				name: this.currentUserService.displayName,
			},
			shareToken: this.args.shareToken,
			isWhitelabeled: this.accountService.whiteLabelingEnabled,
			// Anonymous header CTAs: mirror shared-chat's login redirect, and the app's signup URL.
			loginUrl: this.navigationService.isMobileApp ? '/m/logout' : '/logout',
			signupUrl: `${this.requestService.websiteUrl}/signup`,
		};

		this.reactService.render(
			this.reactElement,
			SharedArtifactEntryPoint(props),
			getShadowDomMode(this.accountService?.email),
		);
	}

	<template>
		<div
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
			id="shared-artifact-container"
			...attributes
		/>
	</template>
}
